/** Shell event bindings — mount-stable listeners (upload input, IMAGE_SELECT, dropdown dismiss) + per-mount workspace bindings (A/B slots, dropdowns, funnel, result actions) + the lightbox. Split from dialog-shell.js (1 file 1 duty). */

import { store } from '../../../core/store.js'
import { eventBus, EVENTS } from '../../../core/event-bus.js'
import { showToast } from '../../../utils/toast.js'
import { trackCardAdopt, trackCardDiscard } from '../../../utils/tracking-funnel.js'
import { useImageAsSource, clearReferenceState } from '../../create-ui/image-roles.js'
import { openPickModal } from '../../create-ui/pick-modal.js'
import { showSourceImage, showReferenceImage, handleImageUpload, handleReferenceUpload } from './image-slots.js'
import { resetResultSlot } from './result-panel.js'
import { openSocialComposer } from '../../social/social-composer.js'

let _fileInputEl = null

// ---- Mount-stable bindings ----

/**
 * Bind listeners that must be registered once per shell mount: prompt input,
 * hidden file input, lightbox close, global dropdown dismiss, IMAGE_SELECT.
 * @param {HTMLElement} container - the mounted workspace root
 */
export function bindShellOnceListeners(container) {
  // Prompt box → creation.textInput (the optional refinement note).
  const promptEl = container.querySelector('#promptInput')
  if (promptEl) promptEl.addEventListener('input', () => store.setState('creation.textInput', promptEl.value))

  _fileInputEl = container.querySelector('#createFileInput')
  if (_fileInputEl) {
    _fileInputEl.addEventListener('change', (e) => {
      const file = e.target.files[0]
      if (file) handleImageUpload(file)
      _fileInputEl.value = ''
    })
  }

  container.querySelector('#createLightboxClose')?.addEventListener('click', closeLightbox)
  container.querySelector('#createLightbox')?.addEventListener('click', (e) => { if (e.target === e.currentTarget) closeLightbox() })

  document.addEventListener('click', () => {
    document.getElementById('modelDropdown')?.classList.add('hidden')
    document.getElementById('aspectDropdown')?.classList.add('hidden')
  })

  // Image picked (library / asset) via image-roles → show in the right slot.
  eventBus.on(EVENTS.IMAGE_SELECT, ({ url, id, role, noVision }) => {
    if (!url || store.getState('mode') !== 'create') return
    if (role === 'reference') { showReferenceImage(url); return }
    // Source picked → READ it (vision). The A image IS the prompt: its vision
    // cards populate the inspiration right-panel (handled on VISION_COMPLETE in
    // create/index.js). why → image-is-the-prompt (the source path, not just B).
    // noVision: the pick is a re-slot of pixels already read (e.g. an external
    // source materialized into an asset at generate time) — don't read it again.
    showSourceImage(url, !noVision, id || null)
  })
}

// ---- Per-mount workspace bindings ----

/**
 * Bind the workspace interaction surface: A/B slot pick/drop, aspect
 * dropdown, funnel CTA, result lightbox, Refine/Keep/Discard, and the Share
 * entry (H-177: re-wired pre-publish social composer after the H-164 removal —
 * the composer is 100% client-side now that the /api/share guard is gone, D4).
 * @param {HTMLElement} container - the mounted workspace root
 */
export function bindWorkspace(container) {
  // A slot: click → pick a source (library/asset typed ref); drag-drop / file upload.
  const src = container.querySelector('#dialogSource')
  if (src) {
    src.addEventListener('click', () => openPickModal('source'))
    src.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPickModal('source') } })
    src.addEventListener('dragover', (e) => { e.preventDefault(); src.classList.add('cw-slot--over') })
    src.addEventListener('dragleave', () => src.classList.remove('cw-slot--over'))
    src.addEventListener('drop', (e) => { e.preventDefault(); src.classList.remove('cw-slot--over'); const f = e.dataTransfer?.files?.[0]; if (f) handleImageUpload(f) })
  }

  // B slot: click → pick a reference; its own file input for upload.
  const ref = container.querySelector('#dialogReference')
  const refInput = container.querySelector('#dialogReferenceInput')
  if (ref) {
    ref.addEventListener('click', (e) => { if (e.target === refInput) return; openPickModal('reference') })
    ref.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPickModal('reference') } })
  }
  if (refInput) refInput.addEventListener('change', (e) => { const f = e.target.files[0]; if (f) handleReferenceUpload(f); refInput.value = '' })

  // aspect dropdown
  container.querySelector('#aspectBtn')?.addEventListener('click', (e) => { e.stopPropagation(); container.querySelector('#aspectDropdown')?.classList.toggle('hidden') })
  container.querySelector('#aspectDropdown')?.addEventListener('click', (e) => {
    const item = e.target.closest('.aspect-item'); if (!item) return
    store.setState('aspectRatio', item.dataset.ratio)
    container.querySelectorAll('#aspectDropdown .aspect-item').forEach(i => i.classList.toggle('active', i === item))
    const lbl = container.querySelector('#aspectBtn .aspect-label'); if (lbl) lbl.textContent = item.dataset.ratio
  })

  // Funnel CTA → hand off to put the design on a product and order it (the only
  // revenue step = the purchase). why → commercial-model §1.
  container.querySelector('#funnelGet')?.addEventListener('click', () => {
    const url = store.getState('creation.result')?.url
    if (!url) return
    eventBus.emit(EVENTS.PUT_ON_PRODUCT, { url })
  })

  // Result slot → lightbox (excluding the action buttons); keyboard-reachable.
  const resultEl = container.querySelector('#dialogResult')
  const openResultLightbox = () => {
    const url = container.querySelector('#dialogResultImg')?.src
    if (url && !url.endsWith('/')) openLightbox(url)
  }
  resultEl?.addEventListener('click', (e) => { if (e.target.closest('.result-action-btn')) return; openResultLightbox() })
  resultEl?.addEventListener('keydown', (e) => {
    if (e.target.closest('.result-action-btn')) return
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openResultLightbox() }
  })

  // Refine / Keep / Discard — per-card adopt/discard signals (backend route
  // /api/customer/cards/:id/{adopt,discard}; adopt is also auto-written on success).
  container.querySelector('#dialogResultContinue')?.addEventListener('click', (e) => {
    e.stopPropagation()
    const result = store.getState('creation.result'); const url = result?.url
    if (!url) return
    ;(Array.isArray(result?.card_ids) ? result.card_ids : []).forEach(id => trackCardAdopt(id))
    if (result?.asset_id) useImageAsSource({ url, id: result.asset_id }, 'asset', { silent: true })
    else showSourceImage(url, false)
    resetResultSlot(); store.setState('creation.result', null); clearReferenceState()
    showToast('Refining this version — pick the next action', 'success', 1800)
  })
  container.querySelector('#dialogResultKeep')?.addEventListener('click', (e) => {
    e.stopPropagation()
    const result = store.getState('creation.result')
    ;(Array.isArray(result?.card_ids) ? result.card_ids : []).forEach(id => trackCardAdopt(id))
    showToast('Saved', 'success', 1500); resetResultSlot(); store.setState('creation.result', null); clearReferenceState()
  })
  container.querySelector('#dialogResultDiscard')?.addEventListener('click', (e) => {
    e.stopPropagation()
    const result = store.getState('creation.result')
    ;(Array.isArray(result?.card_ids) ? result.card_ids : []).forEach(id => trackCardDiscard(id, null))
    resetResultSlot(); store.setState('creation.result', null); clearReferenceState()
  })

  // Share (H-177 entry B): open the client social composer on the C result —
  // pre-publish by design (the share is a traffic channel, not a purchase).
  container.querySelector('#dialogResultShare')?.addEventListener('click', (e) => {
    e.stopPropagation()
    const result = store.getState('creation.result')
    const url = result?.url
    if (!url) return
    openSocialComposer({ url, id: result.asset_id ?? result.generation_id ?? null })
  })
}

// ---- Lightbox ----

function openLightbox(url) {
  const lb = document.getElementById('createLightbox')
  const lbImg = document.getElementById('createLightboxImg')
  if (!lb || !lbImg || !url) return
  lbImg.src = url; lb.classList.remove('hidden')
}
function closeLightbox() { document.getElementById('createLightbox')?.classList.add('hidden') }
